// src/ConsumerApp/api/addresses/deliveryAddress.hooks.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DeliveryAddressService } from './deliveryAddress.service';

/**
 * Fetch all delivery addresses for a customer
 */
export const useGetDeliveryAddresses = (customerId) => {
  return useQuery({
    queryKey: ['deliveryAddresses', customerId],
    queryFn: async () => {
      const data = await DeliveryAddressService.getDeliveryAddresses(customerId);
      const list = data?.data || data?.addresses || data;
      return Array.isArray(list) ? list : [];
    },
    enabled: Boolean(customerId),
    staleTime: 1000 * 60 * 5, // 5 minutes cache
  });
};

/**
 * Fetch single delivery address by ID (Used by OrdersMasterDetailView)
 */
export const useGetDeliveryAddressById = (addressId) => {
  return useQuery({
    queryKey: ['deliveryAddress', addressId],
    queryFn: async () => {
      const data = await DeliveryAddressService.getDeliveryAddressById(addressId);
      return data?.data?.address || data?.data || data?.address || data;
    },
    enabled: Boolean(addressId),
    staleTime: 1000 * 60 * 5,
  });
};

/**
 * Select active/default delivery address
 */
export const useSelectDeliveryAddress = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ customer_id, delivery_address_id }) => {
      return DeliveryAddressService.updateDeliveryAddress({
        addressId: delivery_address_id,
        payload: { customer_id, is_default: true },
      });
    },
    onSuccess: (_, variables) => {
      // Invalidate address lists and the default address
      queryClient.invalidateQueries({ queryKey: ['defaultDeliveryAddress', variables.customer_id] });
      queryClient.invalidateQueries({ queryKey: ['deliveryAddresses', variables.customer_id] });
      queryClient.invalidateQueries({ queryKey: ['deliveryAddress', variables.delivery_address_id] });
      
      // Invalidate bucket & pricing so delivery charges recalculate immediately
      queryClient.invalidateQueries({ queryKey: ['bucketDetails'] });
      queryClient.invalidateQueries({ queryKey: ['bucketSummary', variables.customer_id] });
    },
  });
};

/**
 * Create a new delivery address
 */
export const useCreateDeliveryAddress = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => DeliveryAddressService.createDeliveryAddress(payload),
    onSuccess: (_, variables) => {
      const cId = variables?.customer_id;
      if (cId) {
        queryClient.invalidateQueries({ queryKey: ['deliveryAddresses', cId] });
        queryClient.invalidateQueries({ queryKey: ['defaultDeliveryAddress', cId] });
      }
    },
  });
};

/**
 * Update an existing delivery address
 */
export const useUpdateDeliveryAddress = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ addressId, payload }) =>
      DeliveryAddressService.updateDeliveryAddress({ addressId, payload }),
    onSuccess: (_, variables) => {
      const cId = variables?.payload?.customer_id;
      if (cId) {
        queryClient.invalidateQueries({ queryKey: ['deliveryAddresses', cId] });
        queryClient.invalidateQueries({ queryKey: ['defaultDeliveryAddress', cId] });
      }
      queryClient.invalidateQueries({ queryKey: ['deliveryAddress', variables.addressId] });
    },
  });
};

/**
 * Delete a delivery address
 */
export const useDeleteDeliveryAddress = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (addressId) => DeliveryAddressService.deleteDeliveryAddress(addressId),
    onSuccess: (_, addressId) => {
      queryClient.invalidateQueries({ queryKey: ['deliveryAddresses'] });
      queryClient.invalidateQueries({ queryKey: ['defaultDeliveryAddress'] });
      queryClient.invalidateQueries({ queryKey: ['deliveryAddress', addressId] });
    },
  });
};